import type { AiCostLimitLevel } from '@Domain/AiCostLimits/AiCostLimitLevel';
import AbstractListView from '@UI/Store/AbstractListView';
import type AiCostLimitView from '@UI/Store/AiCostLimits/Views/AiCostLimitView';

const LEVEL_ORDER: readonly AiCostLimitLevel[] = ['account', 'organization', 'facility', 'user'];

/** Immutable View: `readonly` properties, frozen at the end of the constructor. */
class AiCostLimitsListView extends AbstractListView<AiCostLimitView> {
	readonly accountId: number;

	constructor(accountId: number, list: readonly AiCostLimitView[]) {
		super(list, 'AiCostLimitsListView');

		this.accountId = accountId;

		Object.freeze(this);
	}

	ofLevel(level: AiCostLimitLevel): readonly AiCostLimitView[] {
		return this.list.filter((limit) => limit.level === level);
	}

	findBySubject(level: AiCostLimitLevel, subjectId: number): AiCostLimitView | null {
		return this.list.find((limit) => limit.level === level && limit.subjectId === subjectId) ?? null;
	}

	add(limit: AiCostLimitView): AiCostLimitsListView {
		const sorted = [...this.list, limit].sort(
			(left, right) =>
				LEVEL_ORDER.indexOf(left.level) - LEVEL_ORDER.indexOf(right.level) ||
				left.subjectId - right.subjectId ||
				left.id - right.id,
		);

		return new AiCostLimitsListView(this.accountId, sorted);
	}

	replace(limit: AiCostLimitView): AiCostLimitsListView {
		return new AiCostLimitsListView(
			this.accountId,
			this.list.map((current) => (current.id === limit.id ? limit : current)),
		);
	}

	remove(limitId: number): AiCostLimitsListView {
		return new AiCostLimitsListView(
			this.accountId,
			this.list.filter((limit) => limit.id !== limitId),
		);
	}
}

export default AiCostLimitsListView;
